import React, { useState } from 'react';
import { FaHeadset, FaPhone, FaEnvelope, FaComments, FaQuestionCircle, FaFileAlt, FaPaperPlane } from 'react-icons/fa';

const SellerSupport = () => {
    const [activeTab, setActiveTab] = useState('tickets');
    const [newTicket, setNewTicket] = useState({ subject: '', category: '', description: '' });

    const tickets = [
        { id: 'TKT001', subject: 'Payment not received', category: 'Payments', status: 'open', created: '2024-12-10', lastUpdate: '2024-12-11' },
        { id: 'TKT002', subject: 'Product listing rejected', category: 'Catalog', status: 'in_progress', created: '2024-12-08', lastUpdate: '2024-12-09' },
        { id: 'TKT003', subject: 'Return policy clarification', category: 'Returns', status: 'resolved', created: '2024-12-05', lastUpdate: '2024-12-06' },
    ];

    const faqs = [
        { question: 'How do I update my bank details?', answer: 'Go to Settings > Bank Details and update your information. Changes take 2-3 days to verify.' },
        { question: 'When do I receive my payouts?', answer: 'Payouts are processed every Tuesday and Friday. Funds arrive within 2-3 business days.' },
        { question: 'How do I handle return requests?', answer: 'Go to Returns section to view and approve/reject return requests within 48 hours.' },
        { question: 'What are the platform fees?', answer: 'Platform fee is 10% of the order value. Additional payment gateway charges may apply.' },
    ];

    const getStatusBadge = (status) => {
        const styles = {
            open: 'bg-yellow-100 text-yellow-800',
            in_progress: 'bg-blue-100 text-blue-800',
            resolved: 'bg-green-100 text-green-800',
            closed: 'bg-gray-100 text-gray-800',
        };
        return styles[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Seller Support</h1>
                <p className="text-gray-600">Get help with your seller account and orders</p>
            </div>

            {/* Contact Options */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white">
                    <FaPhone className="text-3xl mb-3" />
                    <h3 className="font-semibold text-lg">Phone Support</h3>
                    <p className="text-blue-100 text-sm mt-1">Mon-Sat, 9AM - 6PM</p>
                    <p className="font-bold text-xl mt-2">1800-123-4567</p>
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white">
                    <FaEnvelope className="text-3xl mb-3" />
                    <h3 className="font-semibold text-lg">Email Support</h3>
                    <p className="text-purple-100 text-sm mt-1">Response within 24 hours</p>
                    <p className="font-bold mt-2">seller@kalingacrafts.com</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
                    <FaComments className="text-3xl mb-3" />
                    <h3 className="font-semibold text-lg">Live Chat</h3>
                    <p className="text-green-100 text-sm mt-1">Available 24/7</p>
                    <button className="mt-2 px-4 py-2 bg-white text-green-600 rounded-lg font-semibold hover:bg-green-50">
                        Start Chat
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
                <button
                    onClick={() => setActiveTab('tickets')}
                    className={`px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2 ${activeTab === 'tickets' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                >
                    <FaFileAlt /> My Tickets
                </button>
                <button
                    onClick={() => setActiveTab('new')}
                    className={`px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2 ${activeTab === 'new' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                >
                    <FaPaperPlane /> New Ticket
                </button>
                <button
                    onClick={() => setActiveTab('faq')}
                    className={`px-6 py-3 rounded-lg font-semibold transition flex items-center gap-2 ${activeTab === 'faq' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                >
                    <FaQuestionCircle /> FAQs
                </button>
            </div>

            {/* Tickets Tab */}
            {activeTab === 'tickets' && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {tickets.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">No support tickets found</div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {tickets.map((ticket) => (
                                    <tr key={ticket.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{ticket.id}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{ticket.subject}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">{ticket.category}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(ticket.status)}`}>
                                                {ticket.status.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{ticket.created}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">View</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* New Ticket Tab */}
            {activeTab === 'new' && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Create Support Ticket</h2>
                    <form className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <select
                                value={newTicket.category}
                                onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Select category</option>
                                <option value="payments">Payments & Payouts</option>
                                <option value="orders">Orders & Shipping</option>
                                <option value="returns">Returns & Refunds</option>
                                <option value="catalog">Catalog & Listings</option>
                                <option value="account">Account Settings</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                            <input
                                type="text"
                                value={newTicket.subject}
                                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                                placeholder="Brief description of your issue"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                rows={4}
                                value={newTicket.description}
                                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                                placeholder="Describe your issue in detail..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                        >
                            Submit Ticket
                        </button>
                    </form>
                </div>
            )}

            {/* FAQ Tab */}
            {activeTab === 'faq' && (
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="bg-white rounded-lg shadow p-4">
                            <div className="flex items-start gap-3">
                                <FaQuestionCircle className="text-blue-500 mt-1" />
                                <div>
                                    <h3 className="font-semibold text-gray-800">{faq.question}</h3>
                                    <p className="text-gray-600 mt-2">{faq.answer}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SellerSupport;
